import React, { type FC, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IconButton, Stack, useTheme } from '@mui/material';

import { SORTING_AND_PAGINATION_KEYS } from '@/hooks/useEntityList';
import { CREATE_MODAL_PARAM, DEFAULT_VALUES_PARAM, EDIT_MODAL_PARAM } from '@/root/linker';
import { borders } from '@/theme/constants';
import { formChangeCounter } from '@/utils/formChangeCounter';
import FilterIcon from '@/views/Filters/assets/filter.svg?react';

interface FilterButtonProps {
  isOpen?: boolean;
  onClick?: () => void;
  compoundFormKeys?: string[];
}

const FilterButton: FC<FilterButtonProps> = ({ isOpen, onClick, compoundFormKeys = [] }) => {
  const { spacing } = useTheme();
  const form = useFormContext();
  const formValue = useWatch();
  const filterChangeCounter = useMemo(
    () =>
      formChangeCounter(formValue, {
        defaultValues: form?.formState?.defaultValues,
        compoundEntriesPaths: compoundFormKeys,
        ignorePaths: [...SORTING_AND_PAGINATION_KEYS, CREATE_MODAL_PARAM, EDIT_MODAL_PARAM],
        ignoreWildcardPaths: [DEFAULT_VALUES_PARAM]
      }),
    [compoundFormKeys, form?.formState?.defaultValues, formValue]
  );

  return (
    <Stack justifyContent="center">
      <Stack position="relative">
        {!!filterChangeCounter && (
          <Stack
            position="absolute"
            justifyContent="center"
            alignItems="center"
            width={spacing(2)}
            height={spacing(2)}
            borderRadius="50%"
            fontSize={spacing(1.5)}
            top={0}
            right={0}
            sx={{
              bgcolor: 'primary.400',
              color: 'background.default',
              transform: 'translate(50%, -50%)',
              zIndex: 1
            }}
          >
            {filterChangeCounter}
          </Stack>
        )}
        <IconButton
          sx={{
            width: spacing(4),
            height: spacing(4),
            border: borders.default,
            borderColor: 'grey.400',
            bgcolor: 'background.default',
            color: 'primary.main',
            borderRadius: spacing(0.5),
            p: 0,
            '&:hover': {
              bgcolor: 'grey.200'
            },
            ...(isOpen && {
              borderColor: 'primary.1000'
            })
          }}
          onClick={onClick}
        >
          <FilterIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export default FilterButton;
